/**
 * Admin — onglet "Créateurs" (Phase 8 + Phase 12).
 *
 * Fonctions :
 * - Liste paginée + recherche par email + stats par plan
 * - Grant/révocation de plan (Basique / Pro / Illimité / Aucun)
 * - Drawer détail : identité, apps, paiements, waitlist, CGU
 * - Reset mot de passe (temporaire affiché UNE FOIS)
 * - Suppression de compte avec confirmation forte (retape email)
 *
 * Toutes les actions mutantes sont loggées côté backend dans `admin_audit`.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Users, Search, ShieldCheck, ShieldOff, X, Crown, Key, Trash2, Copy,
  Globe, EyeOff, ExternalLink, AlertTriangle,
} from 'lucide-react';
import {
  adminListCreators,
  adminGrantPlan,
  adminCreatorDetail,
  adminResetCreatorPassword,
  adminDeleteCreator,
} from '@/lib/api';

const PLAN_LABELS = { none: 'Aucun', basique: 'Basique', pro: 'Pro', illimite: 'Fondateur' };
const PLAN_COLORS = { none: '#8892A6', basique: '#0EA5E9', pro: '#8B5CF6', illimite: '#D4A017' };
const SOURCE_LABELS = { stripe: 'Stripe', admin: 'Admin' };

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function PlanBadge({ plan }) {
  const color = PLAN_COLORS[plan] || PLAN_COLORS.none;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ color, background: `${color}18` }}
    >
      {plan === 'illimite' && <Crown size={9} strokeWidth={2.6} />}
      {PLAN_LABELS[plan] || plan}
    </span>
  );
}

export default function CreatorsTab() {
  const [data, setData] = useState({ items: [], total: 0, by_plan: {} });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [detailFor, setDetailFor] = useState(null); // user id
  const limit = 20;

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminListCreators({ page, limit, q });
      setData(res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const runSearch = (ev) => {
    ev?.preventDefault?.();
    setPage(1);
    load();
  };

  const applyGrant = async (userId, plan, interval) => {
    try {
      const res = await adminGrantPlan(userId, { plan, interval });
      if (res.ok) {
        toast.success(`Plan ${PLAN_LABELS[plan] || plan} attribué.`);
        setData((d) => ({
          ...d,
          items: d.items.map((it) => (it.id === userId ? { ...it, ...res.user } : it)),
        }));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Impossible d'attribuer le plan.");
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));

  return (
    <div className="space-y-6" data-testid="admin-creators-panel">
      {/* Overview */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <div className="rounded-2xl border border-[var(--om-border)] bg-white p-4" data-testid="admin-creators-total">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Créateurs</div>
          <div className="mt-1 flex items-center gap-1.5 text-2xl font-extrabold tabular-nums text-[var(--om-ink)]">
            <Users size={14} strokeWidth={2.4} className="text-[var(--om-muted-2)]" />
            {data.total}
          </div>
        </div>
        {['none', 'basique', 'pro', 'illimite'].map((p) => (
          <div
            key={p}
            className="rounded-2xl border border-[var(--om-border)] bg-white p-4"
            data-testid={`admin-creators-stat-${p}`}
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{PLAN_LABELS[p]}</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums" style={{ color: PLAN_COLORS[p] }}>
              {data.by_plan?.[p] || 0}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={runSearch} className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} strokeWidth={2.4} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--om-muted-2)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un email…"
            className="w-full rounded-full border border-[var(--om-border)] bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--om-ink)]/10"
            data-testid="admin-creators-search-input"
          />
        </div>
        <button
          type="submit"
          className="rounded-full bg-[var(--om-ink)] px-4 py-2 text-xs font-bold text-white"
          data-testid="admin-creators-search-btn"
        >
          Chercher
        </button>
      </form>

      <div className="overflow-hidden rounded-2xl border border-[var(--om-border)] bg-white">
        <table className="min-w-full divide-y divide-[var(--om-border)]" data-testid="admin-creators-table">
          <thead className="bg-[var(--om-bg)]">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Email</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Plan</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Quota</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Source</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Inscrit le</th>
              <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--om-border)]">
            {(data.items || []).map((it) => (
              <tr key={it.id} data-testid={`admin-creator-row-${it.id}`}>
                <td className="whitespace-nowrap px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setDetailFor(it.id)}
                    className="text-sm font-medium text-[var(--om-ink)] hover:underline"
                    data-testid={`admin-creator-open-${it.id}`}
                  >
                    {it.email}
                  </button>
                </td>
                <td className="whitespace-nowrap px-4 py-3"><PlanBadge plan={it.plan} /></td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--om-muted)] tabular-nums">
                  {it.plan === 'illimite' ? '∞' : (it.app_quota || 0)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--om-muted)]">
                  {it.plan_source ? SOURCE_LABELS[it.plan_source] || it.plan_source : '—'}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--om-muted)]">{formatDate(it.created_at)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => applyGrant(it.id, 'basique', 'month')}
                      className="rounded-full border border-[var(--om-border)] bg-white px-2.5 py-1 text-[10px] font-bold text-[var(--om-ink)] hover:bg-[var(--om-bg)]"
                      data-testid={`admin-creator-grant-basique-${it.id}`}
                    >
                      <ShieldCheck size={10} strokeWidth={2.4} className="inline mr-1" /> Basique
                    </button>
                    <button
                      type="button"
                      onClick={() => applyGrant(it.id, 'pro', 'month')}
                      className="rounded-full px-2.5 py-1 text-[10px] font-bold text-white"
                      style={{ background: PLAN_COLORS.pro }}
                      data-testid={`admin-creator-grant-pro-${it.id}`}
                    >
                      <ShieldCheck size={10} strokeWidth={2.4} className="inline mr-1" /> Pro
                    </button>
                    <button
                      type="button"
                      onClick={() => applyGrant(it.id, 'illimite', null)}
                      className="rounded-full px-2.5 py-1 text-[10px] font-bold text-white"
                      style={{ background: PLAN_COLORS.illimite }}
                      title="Accès Fondateur (illimité — usage interne)"
                      data-testid={`admin-creator-grant-illimite-${it.id}`}
                    >
                      <Crown size={10} strokeWidth={2.4} className="inline mr-1" /> Illimité
                    </button>
                    <button
                      type="button"
                      onClick={() => applyGrant(it.id, 'none', null)}
                      className="rounded-full border border-[var(--om-border)] bg-white px-2.5 py-1 text-[10px] font-bold text-[var(--om-muted)] hover:bg-[var(--om-bg)]"
                      data-testid={`admin-creator-revoke-${it.id}`}
                    >
                      <ShieldOff size={10} strokeWidth={2.4} className="inline mr-1" /> Révoquer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {(!data.items || data.items.length === 0) && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-[var(--om-muted-2)]" data-testid="admin-creators-empty">
                  Aucun créateur pour l'instant.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-[var(--om-muted-2)]">
        <div>Page {page} / {totalPages}</div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-full border border-[var(--om-border)] bg-white px-3 py-1.5 font-semibold text-[var(--om-ink)] disabled:opacity-50"
          >
            Précédent
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-full border border-[var(--om-border)] bg-white px-3 py-1.5 font-semibold text-[var(--om-ink)] disabled:opacity-50"
          >
            Suivant
          </button>
        </div>
      </div>

      {detailFor && (
        <CreatorDetailDrawer
          userId={detailFor}
          onClose={() => setDetailFor(null)}
          onChanged={() => load()}
          onDeleted={() => { setDetailFor(null); load(); }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// Drawer détail créateur                                             //
// ------------------------------------------------------------------ //
function CreatorDetailDrawer({ userId, onClose, onChanged, onDeleted }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteEmail, setConfirmDeleteEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [resetting, setResetting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await adminCreatorDetail(userId);
      setDetail(res);
    } catch (err) {
      toast.error("Impossible de charger le détail du créateur.");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId]);

  const doReset = async () => {
    if (resetting) return;
    if (!window.confirm(
      `Générer un nouveau mot de passe temporaire pour ${detail?.user?.email} ?\n` +
      `L'ancien mot de passe cessera de fonctionner immédiatement.`
    )) return;
    setResetting(true);
    try {
      const res = await adminResetCreatorPassword(userId);
      setTempPassword(res.temporary_password);
      toast.success('Mot de passe réinitialisé. Copiez-le maintenant.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset impossible.");
    } finally {
      setResetting(false);
    }
  };

  const copyPwd = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      toast.success('Mot de passe copié.');
    } catch {
      toast.error("Copie impossible — sélectionnez et copiez manuellement.");
    }
  };

  const doDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await adminDeleteCreator(userId, { email_confirmation: confirmDeleteEmail });
      toast.success(`${detail?.user?.email} supprimé.`);
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible.");
    } finally {
      setDeleting(false);
    }
  };

  const user = detail?.user;
  const emailMatch = user && confirmDeleteEmail.trim().toLowerCase() === (user.email || '').toLowerCase();

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      data-testid="admin-creator-drawer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <aside className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--om-border)] bg-white px-6 py-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">
              Détail créateur
            </div>
            <h2 className="mt-0.5 truncate text-lg font-extrabold text-[var(--om-ink)]" data-testid="admin-creator-drawer-email">
              {user?.email || '…'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-full border border-[var(--om-border)] text-[var(--om-muted)] hover:bg-[var(--om-bg)]"
            data-testid="admin-creator-drawer-close"
            aria-label="Fermer"
          >
            <X size={14} strokeWidth={2.4} />
          </button>
        </header>

        {loading || !detail ? (
          <div className="p-8 text-center text-sm text-[var(--om-muted-2)]">Chargement…</div>
        ) : (
          <div className="space-y-5 p-6">
            {/* Identité */}
            <section className="rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg)] p-5" data-testid="admin-creator-detail-identity">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Identité</div>
              <div className="mt-2 space-y-1.5 text-xs text-[var(--om-muted)]">
                <div><span className="text-[var(--om-muted-2)]">ID :</span> <span className="font-mono text-[var(--om-ink)]">{user.id}</span></div>
                <div><span className="text-[var(--om-muted-2)]">Langue :</span> <span className="font-bold text-[var(--om-ink)]">{(user.lang || 'fr').toUpperCase()}</span></div>
                <div><span className="text-[var(--om-muted-2)]">Inscrit le :</span> <span className="text-[var(--om-ink)]">{formatDate(user.created_at)}</span></div>
                <div>
                  <span className="text-[var(--om-muted-2)]">CGU acceptées :</span>{' '}
                  {user.accepted_terms_at ? (
                    <span className="text-emerald-700">Oui ({formatDate(user.accepted_terms_at)})</span>
                  ) : (
                    <span className="text-rose-600">Non</span>
                  )}
                </div>
                {detail.in_waitlist && (
                  <div><span className="text-[var(--om-muted-2)]">Waitlist :</span> <span className="text-emerald-700">Présent</span></div>
                )}
              </div>
            </section>

            {/* Plan */}
            <section className="rounded-2xl border border-[var(--om-border)] bg-white p-5" data-testid="admin-creator-detail-plan">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Plan</div>
                <PlanBadge plan={user.plan} />
              </div>
              <div className="mt-2 space-y-1.5 text-xs text-[var(--om-muted)]">
                <div>
                  <span className="text-[var(--om-muted-2)]">Quota :</span>{' '}
                  <span className="font-bold text-[var(--om-ink)]">
                    {user.plan === 'illimite' ? 'Illimité (∞)' : (user.app_quota || 0)}
                  </span>
                </div>
                <div><span className="text-[var(--om-muted-2)]">Source :</span> <span className="text-[var(--om-ink)]">{user.plan_source ? (SOURCE_LABELS[user.plan_source] || user.plan_source) : '—'}</span></div>
                <div><span className="text-[var(--om-muted-2)]">Activé le :</span> <span className="text-[var(--om-ink)]">{formatDate(user.plan_activated_at)}</span></div>
                {user.interval && (
                  <div><span className="text-[var(--om-muted-2)]">Périodicité :</span> <span className="text-[var(--om-ink)]">{user.interval === 'year' ? 'Annuelle' : 'Mensuelle'}</span></div>
                )}
              </div>
            </section>

            {/* Apps */}
            <section className="rounded-2xl border border-[var(--om-border)] bg-white p-5" data-testid="admin-creator-detail-apps">
              <div className="flex items-baseline justify-between">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">
                  Applications ({detail.apps_count})
                </div>
                <div className="text-[10px] text-[var(--om-muted-2)]">
                  {detail.published_count} publiées · {detail.flagged_count} flag · {detail.submissions_count} soumissions
                </div>
              </div>
              {detail.apps.length === 0 ? (
                <div className="mt-3 text-xs text-[var(--om-muted-2)]">Aucune application.</div>
              ) : (
                <ul className="mt-3 divide-y divide-[var(--om-border)]">
                  {detail.apps.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 py-2" data-testid={`admin-creator-app-${a.id}`}>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                          a.status === 'published' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-[var(--om-ink)]/5 text-[var(--om-muted)]'
                        }`}
                      >
                        {a.status === 'published' ? <Globe size={8} strokeWidth={2.6} /> : <EyeOff size={8} strokeWidth={2.6} />}
                        {a.status === 'published' ? 'Publiée' : 'Brouillon'}
                      </span>
                      {a.is_flagged && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-bold text-rose-700" title="Flaggée">
                          <AlertTriangle size={8} strokeWidth={2.6} /> Flag
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-bold text-[var(--om-ink)]">{a.name}</div>
                        <div className="truncate text-[10px] font-mono text-[var(--om-muted-2)]">/app/{a.slug}</div>
                      </div>
                      {a.status === 'published' && (
                        <a
                          href={`/app/${a.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="grid h-6 w-6 place-items-center rounded-full text-[var(--om-muted)] hover:bg-[var(--om-bg)] hover:text-[var(--om-ink)]"
                          title="Voir la page publique"
                        >
                          <ExternalLink size={11} strokeWidth={2.4} />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Paiements */}
            <section className="rounded-2xl border border-[var(--om-border)] bg-white p-5" data-testid="admin-creator-detail-payments">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">
                Paiements Stripe ({detail.payments.length})
              </div>
              {detail.payments.length === 0 ? (
                <div className="mt-3 text-xs text-[var(--om-muted-2)]">Aucun paiement enregistré pour cet utilisateur.</div>
              ) : (
                <ul className="mt-3 divide-y divide-[var(--om-border)]">
                  {detail.payments.map((p) => (
                    <li key={p.session_id || p.id} className="flex items-center justify-between gap-3 py-2 text-xs">
                      <div className="min-w-0">
                        <div className="font-bold text-[var(--om-ink)]">
                          {(p.plan || '—')} · {(p.interval || '—')}
                        </div>
                        <div className="text-[10px] text-[var(--om-muted-2)]">{formatDate(p.created_at)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold tabular-nums text-[var(--om-ink)]">
                          {((p.amount || 0) / 100).toFixed(2)} {(p.currency || 'eur').toUpperCase()}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--om-muted-2)]">{p.status || '—'}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Actions sensibles */}
            <section className="rounded-2xl border border-rose-500/25 bg-rose-500/5 p-5" data-testid="admin-creator-detail-danger">
              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Actions sensibles</div>

              {/* Reset password */}
              <div className="mt-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-[var(--om-ink)]">Réinitialiser le mot de passe</div>
                    <div className="text-[11px] text-[var(--om-muted)]">
                      Génère un mot de passe temporaire fort. Affiché une seule fois.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={doReset}
                    disabled={resetting}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-ink)] bg-white px-3 py-1.5 text-xs font-bold text-[var(--om-ink)] hover:bg-[var(--om-bg)] disabled:opacity-60"
                    data-testid="admin-creator-reset-btn"
                  >
                    <Key size={11} strokeWidth={2.4} />
                    {resetting ? 'Reset…' : 'Réinitialiser'}
                  </button>
                </div>
                {tempPassword && (
                  <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid="admin-creator-temp-password">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">
                      Mot de passe temporaire — visible UNE FOIS
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <code className="flex-1 select-all break-all rounded-lg bg-white px-2 py-1.5 font-mono text-xs text-[var(--om-ink)]">
                        {tempPassword}
                      </code>
                      <button
                        type="button"
                        onClick={copyPwd}
                        className="inline-flex items-center gap-1 rounded-full bg-[var(--om-ink)] px-3 py-1.5 text-[10px] font-bold text-white"
                        data-testid="admin-creator-copy-password"
                      >
                        <Copy size={10} strokeWidth={2.6} /> Copier
                      </button>
                    </div>
                    <div className="mt-2 text-[10px] leading-relaxed text-amber-900">
                      Communiquez ce mot de passe au créateur par un canal sécurisé.
                      Il ne sera plus jamais affiché ici.
                    </div>
                  </div>
                )}
              </div>

              {/* Delete */}
              <div className="mt-5 border-t border-rose-500/20 pt-4">
                <div className="text-sm font-bold text-rose-700">Supprimer le compte</div>
                <div className="mt-1 text-[11px] leading-relaxed text-[var(--om-muted)]">
                  Cascade complète : applications, soumissions et visites de ce créateur sont
                  supprimées. Les paiements Stripe restent en base pour la comptabilité (marqués orphelins).
                  Retapez <span className="font-bold text-[var(--om-ink)]">{user.email}</span> pour confirmer.
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="email"
                    value={confirmDeleteEmail}
                    onChange={(e) => setConfirmDeleteEmail(e.target.value)}
                    placeholder={user.email}
                    className="flex-1 rounded-full border border-rose-500/40 bg-white px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-rose-500/25"
                    data-testid="admin-creator-delete-confirm-input"
                  />
                  <button
                    type="button"
                    onClick={doDelete}
                    disabled={!emailMatch || deleting}
                    className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="admin-creator-delete-btn"
                  >
                    <Trash2 size={11} strokeWidth={2.6} />
                    {deleting ? 'Suppression…' : 'Supprimer définitivement'}
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}
